import React from 'react';
import { ArrowLeft, ShieldCheck, Search, BarChart3, Zap, FileText, Globe, Target, TrendingUp, CheckCircle2 } from 'lucide-react';
import { t, getCurrentLanguage, getLanguageCode } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface ServicesPageProps {
  onBack: () => void;
  onNavigate?: (page: string) => void;
}

export const ServicesPage: React.FC<ServicesPageProps> = ({ onBack, onNavigate }) => {
  const currentLang = getCurrentLanguage();
  const langCode = getLanguageCode(currentLang) as 'en' | 'fr' | 'ar' | 'de' | 'es';
  
  // SEO optimized for commercial keywords (EN & FR for USA market)
  const seoContent = {
    en: {
      title: 'Professional SEO & AEO Audit Services | Buy SEO Tools Online - Orcaseotools',
      description: 'Buy professional SEO tools, AEO audit services, and GEO optimization software. Get comprehensive website analysis, keyword research, competitor analysis, and SEO consulting. Trusted worldwide for enterprise SEO solutions.',
      keywords: 'buy SEO tools, professional SEO audit services, AEO audit tools, GEO optimization software, website SEO analysis, keyword research tools, competitor analysis tools, SEO consulting services, enterprise SEO solutions, best SEO software, SEO optimization tools, technical SEO audit, on-page SEO tools, off-page SEO analysis, SEO tools USA, buy SEO software online'
    },
    fr: {
      title: 'Services d\'Audit SEO & AEO Professionnels | Acheter Outils SEO en Ligne - Orcaseotools',
      description: 'Achetez des outils SEO professionnels, services d\'audit AEO et logiciels d\'optimisation GEO. Obtenez une analyse complète de site web, recherche de mots-clés, analyse concurrentielle et conseil SEO. Fiable dans le monde entier pour solutions SEO entreprise.',
      keywords: 'acheter outils SEO, services audit SEO professionnel, outils audit AEO, logiciel optimisation GEO, analyse SEO site web, outils recherche mots-clés, outils analyse concurrente, services conseil SEO, solutions SEO entreprise, meilleur logiciel SEO, outils optimisation SEO, audit SEO technique, outils SEO on-page, analyse SEO off-page, outils SEO USA, acheter logiciel SEO en ligne'
    }
  };
  
  const seo = seoContent[langCode] || seoContent.en;
  
  useSEOMetaTags({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonicalUrl: 'https://orcaseotools.com/services',
    language: langCode,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "SEO and AEO Audit Services",
      "provider": {
        "@type": "Organization",
        "name": "sofiahost LLC",
        "legalName": "sofiahost LLC"
      },
      "areaServed": "Worldwide",
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceUrl": "https://orcaseotools.com",
        "serviceType": "Online"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      }
    }
  });

  const services = [
    {
      icon: ShieldCheck,
      title: 'Professional SEO Audit Tools',
      description: 'Comprehensive website SEO analysis with detailed reports covering technical SEO, on-page optimization, and performance metrics. Get actionable insights to improve your search engine rankings.',
      features: ['Technical SEO Analysis', 'On-Page SEO Audit', 'Performance Metrics', 'Mobile Optimization Check', 'Core Web Vitals Analysis'],
      keywords: 'SEO audit tools, website SEO checker, technical SEO analysis, SEO audit software'
    },
    {
      icon: Search,
      title: 'Advanced Keyword Research Tools',
      description: 'Discover high-value keywords with commercial intent using our AI-powered keyword research tools. Analyze search volume, competition, and keyword difficulty to optimize your content strategy.',
      features: ['Keyword Discovery', 'Search Volume Analysis', 'Competition Analysis', 'Keyword Difficulty Score', 'Long-tail Keyword Research'],
      keywords: 'keyword research tools, SEO keyword finder, keyword analysis software, keyword planner alternative'
    },
    {
      icon: BarChart3,
      title: 'Competitor Analysis & Benchmarking',
      description: 'Analyze your competitors\' SEO strategies, backlink profiles, and content performance. Identify opportunities to outperform your competition in search rankings.',
      features: ['Competitor SEO Analysis', 'Backlink Profile Analysis', 'Content Gap Analysis', 'Ranking Comparison', 'Market Share Analysis'],
      keywords: 'competitor analysis tools, SEO competitor research, competitor SEO audit, market analysis tools'
    },
    {
      icon: Zap,
      title: 'AEO (Answer Engine Optimization) Tools',
      description: 'Optimize your content for answer engines like ChatGPT, Google Bard, and other AI-powered search platforms. Get ahead of the curve with AEO optimization strategies.',
      features: ['AEO Content Optimization', 'AI Search Optimization', 'Answer Engine Ranking', 'Voice Search Optimization', 'Featured Snippet Optimization'],
      keywords: 'AEO tools, answer engine optimization, AI search optimization, ChatGPT optimization'
    },
    {
      icon: Globe,
      title: 'GEO (Geographic SEO) Optimization',
      description: 'Optimize your website for local and international search results. Target specific geographic markets with location-based SEO strategies and local search optimization.',
      features: ['Local SEO Optimization', 'International SEO', 'Geographic Targeting', 'Local Citation Building', 'Map Pack Optimization'],
      keywords: 'GEO optimization, local SEO tools, geographic SEO, international SEO services, local search optimization'
    },
    {
      icon: TrendingUp,
      title: 'SERP Rank Tracking & Monitoring',
      description: 'Track your keyword rankings across multiple search engines and locations. Monitor your SEO performance with real-time rank tracking and historical data analysis.',
      features: ['Multi-Engine Tracking', 'Historical Rank Data', 'Ranking Alerts', 'Competitor Tracking', 'Performance Reports'],
      keywords: 'rank tracking tools, SERP monitoring, keyword rank tracker, SEO rank checker, position tracking'
    },
    {
      icon: FileText,
      title: 'SEO Reporting & Analytics',
      description: 'Generate comprehensive SEO reports with actionable insights. Export professional PDF reports for clients and stakeholders with detailed analytics and recommendations.',
      features: ['Custom SEO Reports', 'PDF Export', 'Analytics Dashboard', 'Performance Metrics', 'Client Reporting'],
      keywords: 'SEO reporting tools, SEO analytics software, SEO report generator, website analytics tools'
    },
    {
      icon: Target,
      title: 'SEO Consulting & Strategy',
      description: 'Get expert SEO consulting services from certified professionals. Receive personalized SEO strategies tailored to your business goals and target market.',
      features: ['Expert SEO Consultation', 'Custom SEO Strategy', 'Implementation Guidance', 'Ongoing Support', 'Performance Review'],
      keywords: 'SEO consulting services, SEO strategy consulting, professional SEO services, SEO expert consultation'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="space-y-12">
          <header className="text-center space-y-4">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Professional SEO & AEO Audit Services
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Buy the best SEO tools and AEO optimization services for your business. Trusted by companies worldwide for comprehensive website analysis, keyword research, and search engine optimization.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400 mt-6">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-400" />
                <span>Worldwide Service</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-400" />
                <span>24/7 Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-400" />
                <span>Enterprise Ready</span>
              </div>
            </div>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-indigo-500 transition-all hover:scale-105 space-y-4"
                >
                  <div className="p-3 bg-indigo-500/20 rounded-xl w-fit">
                    <Icon size={24} className="text-indigo-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white">{service.title}</h2>
                  <p className="text-slate-300 text-sm leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-slate-400 text-xs flex items-start gap-2">
                        <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>

          <section className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-2xl p-8 space-y-6">
            <h2 className="text-3xl font-bold text-center">Why Choose Orcaseotools for SEO Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center space-y-2">
                <div className="text-4xl font-bold text-indigo-400">150+</div>
                <div className="text-slate-300">Countries Served</div>
              </div>
              <div className="text-center space-y-2">
                <div className="text-4xl font-bold text-indigo-400">10,000+</div>
                <div className="text-slate-300">Websites Analyzed</div>
              </div>
              <div className="text-center space-y-2">
                <div className="text-4xl font-bold text-indigo-400">4.8/5</div>
                <div className="text-slate-300">Customer Rating</div>
              </div>
            </div>
          </section>

          <section className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-4">
            <h2 className="text-2xl font-bold">Get Started with Our SEO Tools Today</h2>
            <p className="text-slate-300">
              Ready to improve your search engine rankings? Start with our free SEO audit or choose from our professional plans designed for businesses of all sizes. Our SEO tools are trusted by marketers, agencies, and enterprises worldwide.
            </p>
            <div className="flex flex-wrap gap-4 mt-6">
              <button 
                onClick={() => onNavigate ? onNavigate('landing') : onBack()}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold transition-colors"
              >
                Start Free Trial
              </button>
              <button 
                onClick={() => onNavigate ? onNavigate('landing') : onBack()}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-bold transition-colors"
              >
                View Pricing Plans
              </button>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
};

